import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { InfoIconTooltip } from '@liveauctioneers/hammer-ui-core/infoIconTooltip';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { OptionType, Select } from '@liveauctioneers/hammer-ui-core/select';
import { BodyPrimary, BodySecondary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import * as React from 'react';

import { RouteButton } from '@/components/RouteButton/RouteButton';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { deleteCertificate, fetchCertificateDownload, getCertificates } from '@/redux/modules/certCapture';
import { USStates } from '@/utils/intl';
import { getCertCapturePageUrl } from '@/utils/urls';

const TaxCertManagementForm = () => {
    const dispatch = useAppDispatch();
    const { formatMessage } = useIntl();
    const taxCertificates = useAppSelector(getCertificates);
    const [shipZone, setShipZone] = React.useState('');

    const onStateChange = ({ value }: OptionType) => {
        setShipZone(value);
    };

    const navTo = getCertCapturePageUrl(shipZone);

    const fetchCertificate: any = React.useCallback((cert) => dispatch(fetchCertificateDownload(cert.id)), [dispatch]);

    const deleteCert = React.useCallback((cert) => dispatch(deleteCertificate(cert.id)), [dispatch]);

    return (
        <div className="w-full">
            {Boolean(taxCertificates.length) && (
                <>
                    <H2 className="mb-xs">
                        <FormattedMessage id="certManagement.taxCerts" />
                    </H2>
                    <BodyPrimary className="mb-sm">
                        <FormattedMessage id="certManagement.manageCerts" />
                    </BodyPrimary>
                    <table className="mt-sm w-full border-collapse">
                        <thead className="xsMax:hidden">
                            <tr className="border-b border-border-primary">
                                <th
                                    className="pb-xs text-left uppercase"
                                    scope="col"
                                >
                                    <BodySecondary>
                                        <FormattedMessage id="certManagement.state" />
                                    </BodySecondary>
                                </th>
                                <th
                                    className="pb-xs text-left uppercase"
                                    scope="col"
                                >
                                    <BodySecondary>
                                        <FormattedMessage id="certManagement.signedDate" />
                                    </BodySecondary>
                                </th>
                                <th
                                    className="pb-xs text-left uppercase"
                                    scope="col"
                                >
                                    <BodySecondary>
                                        <FormattedMessage id="certManagement.expirationDate" />
                                    </BodySecondary>
                                </th>
                                <th
                                    className="pb-xs text-left uppercase"
                                    colSpan={4}
                                    scope="col"
                                >
                                    <BodySecondary>
                                        <FormattedMessage id="certManagement.status" />
                                    </BodySecondary>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {taxCertificates.map((cert, index) => {
                                const isValid = cert.valid && cert.status !== 'REVOKED';
                                const isComplete = cert.status === 'COMPLETE';

                                return (
                                    <tr
                                        className="cursor-pointer border-b border-border-primary last:border-b-0 hover:bg-background-hover xsMax:flex xsMax:flex-col xsMax:gap-xs xsMax:border-b xsMax:py-sm"
                                        key={`tax-cert-${index}`}
                                    >
                                        <td className="py-sm xsMax:flex xsMax:justify-between xsMax:py-0">
                                            <BodySecondary className="desktop:hidden xsMax:inline">
                                                <FormattedMessage id="certManagement.state" />
                                            </BodySecondary>
                                            <BodyPrimary>{cert.state}</BodyPrimary>
                                        </td>
                                        <td className="py-sm xsMax:flex xsMax:justify-between xsMax:py-0">
                                            <BodySecondary className="desktop:hidden xsMax:inline">
                                                <FormattedMessage id="certManagement.signedDate" />
                                            </BodySecondary>
                                            <BodyPrimary>{cert.signed}</BodyPrimary>
                                        </td>
                                        <td className="py-sm xsMax:flex xsMax:justify-between xsMax:py-0">
                                            <BodySecondary className="desktop:hidden xsMax:inline">
                                                <FormattedMessage id="certManagement.expirationDate" />
                                            </BodySecondary>
                                            <BodyPrimary>{cert.expiration}</BodyPrimary>
                                        </td>
                                        <td className="py-sm xsMax:flex xsMax:justify-between xsMax:py-0">
                                            <BodySecondary className="desktop:hidden xsMax:inline">
                                                <FormattedMessage id="certManagement.status" />
                                            </BodySecondary>
                                            <div className="flex items-center gap-xs">
                                                {isValid ? (
                                                    isComplete ? (
                                                        <Badge
                                                            color="success"
                                                            emphasis="bold"
                                                            size="sm"
                                                        >
                                                            <FormattedMessage id="certManagement.valid" />
                                                        </Badge>
                                                    ) : (
                                                        <>
                                                            <Badge
                                                                color="neutral"
                                                                size="sm"
                                                            >
                                                                <FormattedMessage id="certManagement.inReview" />
                                                            </Badge>
                                                            <InfoIconTooltip
                                                                color="primary"
                                                                iconStyle="fill"
                                                                id={`pending-info-${cert.id}`}
                                                                size="sm"
                                                            >
                                                                {formatMessage({
                                                                    id: 'certManagement.reviewingCertificate',
                                                                })}
                                                            </InfoIconTooltip>
                                                        </>
                                                    )
                                                ) : (
                                                    <>
                                                        <Badge
                                                            color="critical"
                                                            emphasis="bold"
                                                            size="sm"
                                                        >
                                                            <FormattedMessage id="certManagement.invalid" />
                                                        </Badge>
                                                        <InfoIconTooltip
                                                            id={`invalid-info-${cert.id}`}
                                                            size="sm"
                                                        >
                                                            {formatMessage({
                                                                id: 'certManagement.invalidCertificate',
                                                            })}
                                                        </InfoIconTooltip>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td
                                            className="py-sm xsMax:py-0 xsMax:text-center"
                                            colSpan={isValid && isComplete ? 1 : 2}
                                        >
                                            <InlineButton onClick={() => fetchCertificate(cert)}>
                                                <FormattedMessage id="certManagement.view" />
                                            </InlineButton>
                                        </td>
                                        {isValid && isComplete ? (
                                            <td className="py-sm xsMax:py-0 xsMax:text-center">
                                                <RouteLink to={`${getCertCapturePageUrl(cert.state)}`}>
                                                    <FormattedMessage id="certManagement.renew" />
                                                </RouteLink>
                                            </td>
                                        ) : null}
                                        <td className="py-sm xsMax:py-0 xsMax:text-center">
                                            <InlineButton onClick={() => deleteCert(cert)}>
                                                {formatMessage({
                                                    id: 'certManagement.delete',
                                                })}
                                            </InlineButton>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            )}
            <div
                className={cn('w-[300px] xsMax:w-full xsMax:[&_button]:w-full', taxCertificates?.length > 0 && 'mt-lg')}
            >
                <H2 className="mb-xs">
                    <FormattedMessage id="certManagement.addCert" />
                </H2>
                <BodyPrimary className="mb-xs inline-block">
                    <FormattedMessage id="certManagement.selectState" />
                </BodyPrimary>
                <Select
                    className="mb-lg"
                    data-testid="taxCertificationSelect"
                    onChange={(option) => onStateChange(option)}
                    options={USStates.map((state) => ({
                        label: state,
                        value: state,
                    }))}
                />
                <RouteButton
                    data-testid="addCertificationButton"
                    disabled={shipZone === ''}
                    to={navTo}
                >
                    {formatMessage({ id: 'certManagement.upload' })}
                </RouteButton>
            </div>
        </div>
    );
};

export default TaxCertManagementForm;
